/**
 * reportSpacingTokens — K2 (spacing / page-rhythm lattice + density).
 *
 * THE single source of truth for the report's VERTICAL RHYTHM (the gap
 * between sections + between blocks within a section), the SIDEBAR GEOMETRY
 * (the confidential rail width + its content clearance — the historical
 * "magic 70px"), and the dotted "datum-strip" DIVIDER styling — shared by
 * the section shells, the page-flow wrappers, the density CSS, and the
 * EditorialDatumStrip primitive so they can never drift.
 *
 * WHY THIS EXISTS
 * ---------------
 * Before K2 the report's vertical rhythm was scattered hardcoded Tailwind
 * literals — `py-8` on most section shells, `py-6` on a few, `gap-6` on the
 * page-flow wrappers, `mb-6` under section headings — and the sidebar
 * clearance was a bare `70px` magic number in reportSectionChrome.ts (with a
 * code comment explaining it was 40px rail + 30px margin). A density change
 * meant hunting every literal; the relationship "70 = 40 + 30" lived only in
 * prose. Routing every surface through ONE token module makes the rhythm a
 * deliberate, gate-locked design decision rather than an emergent average of
 * whatever each section happened to ship.
 *
 * K2 DENSIFICATION
 * ----------------
 * The reference redesign (`~/Desktop/Redhead_BTM_Feasibility_Redesign_1.pdf`,
 * "Reactor Datum") is deliberately DENSE — tight vertical rhythm, datum-strip
 * dividers between bands, no white gaps. K2 tightens the DEFAULT
 * (comfortable) section vertical padding from the historical `py-8` (2rem) to
 * `py-6` (1.5rem) — a 25% reduction that pairs with the J0/J1 measured paging
 * (the deck owns page breaks now, so we no longer need fat section padding to
 * absorb break slack). The `compact` density notch tightens further still
 * (1.25rem, the value the prior `.report-density-compact` rule already used).
 * Both keep our fonts/colours; only the lattice tightens.
 *
 * UNITS — `rem` for the on-screen/print vertical rhythm (the screen's native
 * unit, scales with the report body size); `px` for the sidebar geometry (the
 * confidential rail is a fixed-pixel absolutely-positioned element — `w-10` =
 * 40px — so its clearance is naturally pixel-denominated). These are the
 * media's native units; the tokens keep each value in one place.
 *
 * NB: index.css cannot import a TS constant. The `.report-density-compact`
 * padding literal there carries `1.25rem` with a comment pointing back here,
 * and the `reportSpacingTokens.test.ts` gate asserts the literal still equals
 * {@link REPORT_DENSITY_COMPACT_PY_REM} so screen / print can't drift.
 */
import { roleColor, type ReportColorRole } from './reportRoleColors';
import type { ReportTheme } from '../types/reportTheme';

// ── SIDEBAR GEOMETRY (kill the magic 70px) ───────────────────────────────
/**
 * The confidential left rail WIDTH in CSS px. Matches the `w-10` utility on
 * {@link import('./reportSectionChrome').CONFIDENTIAL_SIDEBAR_CLASS} (Tailwind
 * `w-10` = 2.5rem = 40px at the 16px root). The rail is absolutely positioned
 * at `left-0`, so in-flow content must clear THIS width plus a breathing
 * margin (below).
 */
export const REPORT_SIDEBAR_RAIL_PX = 40;

/**
 * The breathing margin in CSS px between the confidential rail's right edge
 * and the start of in-flow content. Previously folded silently into the bare
 * `70px` literal (70 = 40 rail + 30 margin); now named.
 */
export const REPORT_SIDEBAR_MARGIN_PX = 30;

/**
 * The TOTAL in-flow left inset (px) that clears the confidential rail — the
 * historical "magic 70px", now DERIVED as rail + margin so the relationship
 * can never silently drift. Consumed by reportSectionChrome.ts to build the
 * `lg:pl-[70px]` clearance class.
 */
export const REPORT_SIDEBAR_CLEARANCE_PX =
  REPORT_SIDEBAR_RAIL_PX + REPORT_SIDEBAR_MARGIN_PX;

/**
 * The standard report side gutter (px) at the `lg` breakpoint — the tightened
 * print inset (71786e33). Scattered as a bare `36px` literal across the three
 * padding helpers in reportSectionChrome.ts; tokenized here so the gutter is
 * one value.
 */
export const REPORT_SIDE_GUTTER_PX = 36;

// ── VERTICAL RHYTHM (section + block spacing) ─────────────────────────────
/**
 * The DEFAULT (comfortable) section vertical padding in `rem`. K2
 * densification: tightened from the historical 2rem (`py-8`) to 1.5rem
 * (`py-6`) so the default report reads as dense as the redesign reference
 * without the fat inter-section white gaps. The deck (J0/J1 measured paging)
 * owns page breaks, so the section no longer needs extra padding to absorb
 * break slack.
 */
export const REPORT_SECTION_PY_REM = 1.5;

/**
 * The COMPACT-density section vertical padding in `rem`. One extra notch
 * tighter than the comfortable default — the value the prior
 * `.report-density-compact` index.css rule already used (1.25rem). index.css
 * carries this literal with a comment pointing here; the gate asserts lockstep.
 */
export const REPORT_DENSITY_COMPACT_PY_REM = 1.25;

/**
 * The inter-section / page-flow vertical gap in `rem` — the `gap-6` (1.5rem)
 * on the page-1/2/3 flow wrappers in SiteRagReportView. Tokenized so the
 * page-flow rhythm matches the section padding rhythm (both 1.5rem) for an
 * even lattice.
 */
export const REPORT_FLOW_GAP_REM = 1.5;

/**
 * The standard block-to-block gap WITHIN a section in `rem` — the `space-y-4`
 * (1rem) used pervasively inside section bodies. Tokenized for completeness so
 * the inner rhythm is nameable; consumers that already ship `space-y-4` map to
 * this value.
 */
export const REPORT_BLOCK_GAP_REM = 1.0;

/**
 * The gap below a section H2 heading in `rem` — the `mb-6` (1.5rem) the
 * specialized-section headings carry. Tokenized so heading rhythm tracks the
 * lattice.
 */
export const REPORT_HEADING_MB_REM = 1.5;

// ── TAILWIND-CLASS HELPERS (so consumers stay declarative) ────────────────
/**
 * Map a `rem` value to its Tailwind spacing-scale step name (`8` = 2rem, `6`
 * = 1.5rem, `5` = 1.25rem, `4` = 1rem) at the 0.25rem-per-step convention.
 * Returns the bare step number as a string. Throws on a non-grid value so a
 * mistyped token surfaces at module load rather than emitting an invalid class.
 */
function remToTwStep(rem: number): string {
  const step = rem / 0.25;
  if (!Number.isInteger(step) || step < 0) {
    throw new Error(`reportSpacingTokens: ${rem}rem is not on the Tailwind 0.25rem grid`);
  }
  return String(step);
}

/**
 * The section vertical-padding utility for the active density. `comfortable`
 * (default) → `py-6` (the K2-tightened default); `compact` → `py-5` (1.25rem).
 * Consumers pass `theme.density` so the section's OWN top/bottom rhythm tracks
 * the token. (The `.report-density-compact` CSS override in index.css is the
 * belt-and-braces enforcement for legacy call sites that still hardcode `py-8`;
 * new call sites should prefer this helper.)
 */
export function reportSectionGapClass(
  density: ReportTheme['density'] | undefined,
): string {
  const rem = density === 'compact' ? REPORT_DENSITY_COMPACT_PY_REM : REPORT_SECTION_PY_REM;
  return `py-${remToTwStep(rem)}`;
}

/** The inter-section page-flow gap utility (`gap-6`). */
export const REPORT_FLOW_GAP_CLASS = `gap-${remToTwStep(REPORT_FLOW_GAP_REM)}`;

/** The within-section block gap utility (`space-y-4`). */
export const REPORT_BLOCK_GAP_CLASS = `space-y-${remToTwStep(REPORT_BLOCK_GAP_REM)}`;

/** The section-heading bottom-margin utility (`mb-6`). */
export const REPORT_HEADING_MB_CLASS = `mb-${remToTwStep(REPORT_HEADING_MB_REM)}`;

/** The standard side-gutter utility at `lg` (`lg:px-[36px]` / `lg:p-[36px]`). */
export const REPORT_SIDE_GUTTER_PX_CSS = `${REPORT_SIDE_GUTTER_PX}px`;

/** The sidebar clearance utility value at `lg` (`70px`). */
export const REPORT_SIDEBAR_CLEARANCE_PX_CSS = `${REPORT_SIDEBAR_CLEARANCE_PX}px`;

// ── DATUM-STRIP DIVIDER (the dotted horizontal band rule) ─────────────────
/**
 * The datum-strip divider STYLE — the dotted horizontal rule the redesign
 * uses between bands. Token-styled: the colour is resolved from a `roleColor`
 * role (`'hair'` for a faint inter-block divider, `'rule'` for a stronger
 * band/section separator) so it follows the active theme in every preset.
 *
 * Returns a `CSSProperties` border declaration (NOT a Tailwind class) because
 * the colour is a resolved hex — inline style bypasses the CSS-variable island
 * exactly like the rest of the editorial primitives (see reportRoleColors.ts).
 * `weight` selects the dotted line thickness in px (default 1).
 *
 * The vertical breathing room around the strip is the caller's (a band
 * separator wants the section gap above/below); this helper styles only the
 * rule itself so it composes with the spacing tokens above.
 */
export function datumStripBorderStyle(
  theme: Partial<ReportTheme> | null | undefined,
  role: Extract<ReportColorRole, 'hair' | 'rule'> = 'rule',
  weight = 1,
): { borderTopWidth: number; borderTopStyle: 'dotted'; borderTopColor: string } {
  return {
    borderTopWidth: weight,
    borderTopStyle: 'dotted',
    borderTopColor: roleColor(theme, role),
  };
}
